import { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  DescriptionUI,
  ModalCWShellUI,
} from '@next-order/next-order-components';
import { eventNames } from 'configuration/eventNames';
import { MenuContext } from 'contexts/MenuProvider';
import { MENU_ITEM_OBJ_TYPE } from 'helpers/enums';
import getImage from 'helpers/getImage';
import { usePromoConfetti } from 'helpers/hooks/usePromoConfetti';

// NOTE: orderSetup does not carry notificationBadgeText yet. Hardcoded until the
// field lands, at which point the `??` fallback below is the only thing that
// needs removing.
const BADGE_TEXT = 'Limited time';
const DISMISS_TEXT = 'No thanks';
const CLOSE_ANIMATION_MS = 300;
const SCROLL_DELAY_MS = 300;
const HIGHLIGHT_DURATION_MS = 3000;

let hasShownThisPageLoad = false;

const parseHttpUrl = (action) => {
  if (typeof action !== 'string' || !action.trim()) return null;

  try {
    const url = new URL(action.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch (_err) {
    return null;
  }
};

const ID_PARAMS = ['specialId', 'menuItemId'];

const readIdParam = (url) => {
  for (const param of ID_PARAMS) {
    const id = url.searchParams.get(param)?.trim();
    if (id) return id;
  }
  return '';
};

const hasIdParam = (url) =>
  ID_PARAMS.some((param) => url.searchParams.has(param));

const eventFor = (itemObjType) =>
  itemObjType === MENU_ITEM_OBJ_TYPE.special
    ? eventNames.SPECIALS_MENU_ITEM_OPENED
    : eventNames.MENU_ITEM_OPENED;

function NotificationModal({
  orderSetup,
  searchCategoriesList,
  onClickItemHandler,
}) {
  const {
    isNotificationOn,
    notificationImage,
    notificationImagePath,
    notificationTitle,
    notificationMessage,
    notificationButtonText,
    notificationButtonAction = 'xJbMv19hkGfMlP9Tpot8',
    notificationBadgeText,
  } = orderSetup || {};
  const { setHighlightedItemId } = useContext(MenuContext);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const image = notificationImagePath || notificationImage;
  const badgeText = notificationBadgeText ?? BADGE_TEXT;

  usePromoConfetti(showModal);

  useEffect(() => {
    if (hasShownThisPageLoad) return;

    if (isNotificationOn && notificationMessage) {
      hasShownThisPageLoad = true;
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotificationOn, notificationMessage]);

  useEffect(() => {
    if (!isClosing) return undefined;

    const timer = setTimeout(() => {
      setShowModal(false);
      setIsClosing(false);
    }, CLOSE_ANIMATION_MS);

    return () => clearTimeout(timer);
  }, [isClosing]);

  const closeModal = () => {
    setIsClosing(true);
  };

  const handleOpenChange = (open) => {
    if (!open) closeModal();
  };

  const findTarget = (id) =>
    (searchCategoriesList ?? [])
      .flatMap((category) => category?.data ?? [])
      .find((item) => item?._id === id);

  const openItem = (item) => {
    onClickItemHandler(item.itemObjType, item, eventFor(item.itemObjType));

    setTimeout(() => {
      const itemElement = document.querySelector(
        `[data-menu-item-id="${item._id}"]`
      );

      if (itemElement) {
        setHighlightedItemId(item._id);

        itemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });

        setTimeout(() => {
          setHighlightedItemId(null);
        }, HIGHLIGHT_DURATION_MS);
      }
    }, SCROLL_DELAY_MS);
  };

  const handleButtonClick = () => {
    const url = parseHttpUrl(notificationButtonAction);
    const target = url
      ? findTarget(readIdParam(url))
      : findTarget(notificationButtonAction?.trim());

    if (target) {
      openItem(target);
    } else if (url && !hasIdParam(url)) {
      window.open(url.href, '_blank', 'noopener,noreferrer');
    }

    closeModal();
  };

  return (
    <>
      {showModal && (
        <ModalCWShellUI
          maxWidth={460}
          open={showModal}
          onOpenChange={handleOpenChange}
          zIndex={9999}
          maxHeight='full'
          isLoading={!!image && isLoading}
          extendedClassName={isClosing ? '!animate-pm-fade-out' : ''}
          extendedOverlayClassName={`backdrop-blur-[3px] ${
            isClosing ? '!animate-pm-fade-out' : ''
          }`}
        >
          <div className='mx-auto mt-3.5 h-[5px] w-11 shrink-0 rounded-0.75 bg-[rgba(20,24,32,0.14)] sm:hidden' />

          <div className='relative flex shrink-0 items-center gap-2 px-5 pt-3'>
            {badgeText && (
              <span className='rounded-full bg-primary/10 px-2.75 py-1.5 text-[11px] font-extrabold uppercase leading-none tracking-[0.1em] text-primary'>
                {badgeText}
              </span>
            )}
          </div>

          {notificationTitle && (
            <ModalCWShellUI.Header
              title={notificationTitle}
              extendedClassName='!pt-2.5 !pb-0'
              extendedHeadingClassName='w-fit max-w-full text-left !text-[29px] !font-extrabold !leading-[1.12] !tracking-[-0.03em] text-[#101620]'
            />
          )}

          <ModalCWShellUI.Body extendedClassName='!pt-2.5 !pb-2'>
            {notificationMessage && (
              <DescriptionUI
                fontSize='custom'
                lineHeight='custom'
                fontWeight='normal'
                bottomMargin='none'
                extendedClassName='max-w-[300px] text-sm leading-[1.5] text-[#6f7787] [text-wrap:pretty]'
              >
                {notificationMessage}
              </DescriptionUI>
            )}

            {!!image && (
              <div className='relative mt-4 w-full overflow-hidden rounded-[22px] bg-[#e9e7e2]'>
                <img
                  src={getImage(image)}
                  alt='notification'
                  className='block h-auto max-h-[70vh] w-full object-contain'
                  onError={(e) => {
                    setIsLoading(false);
                    if (
                      notificationImage &&
                      e.target.src !== notificationImage
                    ) {
                      e.target.src = notificationImage;
                    }
                  }}
                  onLoad={() => {
                    setIsLoading(false);
                  }}
                />
              </div>
            )}
          </ModalCWShellUI.Body>

          <ModalCWShellUI.Footer>
            <div className='w-full'>
              {notificationButtonText && (
                <div className='motion-safe:animate-pm-cta-float motion-safe:hover:[animation-play-state:paused]'>
                  <div className='promo-sheet-cta relative rounded-xl'>
                    <ModalCWShellUI.Button
                      onClick={handleButtonClick}
                      extendedClassName='!font-extrabold !tracking-[-0.01em]'
                    >
                      {notificationButtonText}
                    </ModalCWShellUI.Button>
                    <div
                      aria-hidden
                      className='pointer-events-none absolute inset-0 overflow-hidden rounded-xl'
                    >
                      <div className='absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.35)] to-transparent motion-safe:animate-pm-sheen' />
                    </div>
                  </div>
                </div>
              )}
              <button
                type='button'
                onClick={closeModal}
                className={`w-full py-1.5 text-center text-[15px] font-semibold text-[#9aa1ad] transition-colors hover:text-[#6f7787] ${
                  notificationButtonText ? 'pt-3' : ''
                }`}
              >
                {DISMISS_TEXT}
              </button>
            </div>
          </ModalCWShellUI.Footer>
        </ModalCWShellUI>
      )}
    </>
  );
}

function mapStateToProps(state) {
  const {
    homeReducer: { orderSetup },
  } = state;

  return {
    orderSetup,
  };
}

export default connect(mapStateToProps, null)(NotificationModal);
